import CheckmarkIcon from '@assets/icons/checkmark-thin.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { AgentAutoPublishCountdown } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-auto-publish-countdown';
import { AgentLastRunLabel } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-last-run-label';
import { AgentTaskCard as AgentTaskCardData } from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import {
  combineActionedCount,
  computeProgressPercent,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/utils/task-progress';
import { createStyles } from '@/contexts/theme-context';
import { DimensionValue, Pressable, View } from 'react-native';
import { formatCurrency } from '@/utils/money';
import { LocalActionCounts } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/count-actions';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useExpirationCountdown } from '@sales-portal/blocs/hooks/use-expiration-countdown';

interface Props {
  card: AgentTaskCardData;
  currencyCode: string;
  localActionCounts: LocalActionCounts; // accepted + overridden + rejected
  onPress: () => void;
}

export const AgentTaskCard: React.FC<Props> = ({
  card,
  currencyCode,
  localActionCounts,
  onPress,
}) => {
  const [styles, theme] = useStyles();

  const {
    acceptedRecommendationsCount,
    autoPublishAt,
    description,
    lastCompletedAt,
    name,
    overriddenRecommendationsCount,
    rejectedRecommendationsCount,
    taskTypeId,
    totalRecommendationsCount,
    weeklyGPImpactInMicros,
  } = card;

  const millisecondsUntilAutoPublish = useExpirationCountdown(autoPublishAt);

  const gpImpact = formatCurrency(weeklyGPImpactInMicros, currencyCode);
  const formattedGpImpact =
    weeklyGPImpactInMicros > 0 ? `+${gpImpact}` : gpImpact;

  const publishedActionedRecommendationsCount =
    acceptedRecommendationsCount +
    overriddenRecommendationsCount +
    rejectedRecommendationsCount;
  const actionedRecommendationsCount = combineActionedCount(
    publishedActionedRecommendationsCount,
    localActionCounts.actionedCount,
    totalRecommendationsCount,
  );
  const unpublishedRecommendationsCount =
    totalRecommendationsCount - publishedActionedRecommendationsCount;
  const progress = computeProgressPercent(
    actionedRecommendationsCount,
    totalRecommendationsCount,
  );
  // Every recommendation is published on the backend (nothing left pending).
  const isTaskCompleted =
    totalRecommendationsCount > 0 &&
    publishedActionedRecommendationsCount === totalRecommendationsCount;

  const widthPercent = (count: number): DimensionValue =>
    totalRecommendationsCount > 0
      ? `${(count / totalRecommendationsCount) * 100}%`
      : 0;
  const segments = [
    {
      color: theme.sk.color.icon.primary,
      count: publishedActionedRecommendationsCount,
    },
    {
      color: theme.sk.color.icon.positive.secondary,
      count: localActionCounts.acceptedCount,
    },
    {
      color: theme.sk.color.icon.blue,
      count: localActionCounts.overriddenCount,
    },
    {
      color: theme.sk.color.icon.negative.secondary,
      count: localActionCounts.rejectedCount,
    },
  ];

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }) => [
        styles.container,
        hovered && styles.containerHovered,
      ]}
      testID={`agent-task-card-${taskTypeId}`}
    >
      <View style={styles.nameColumn}>
        <Typography numberOfLines={1} variant="heading-sm">
          {name}
        </Typography>
        <AgentLastRunLabel lastCompletedAt={lastCompletedAt} />
      </View>
      <View style={styles.descriptionColumn}>
        {description ? (
          <Typography
            color={theme.sk.color.text.tertiary}
            numberOfLines={2}
            variant="paragraph-md"
          >
            {description}
          </Typography>
        ) : null}
      </View>
      <View style={styles.gpImpactColumn}>
        <Typography variant="heading-sm">{formattedGpImpact}</Typography>
      </View>
      <View style={styles.recCountColumn}>
        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-md"
        >
          {i18n.t(
            unpublishedRecommendationsCount === 1
              ? 'pricing.agent.tasks.rec_count_singular'
              : 'pricing.agent.tasks.rec_count_plural',
            {
              count: unpublishedRecommendationsCount,
            },
          )}
        </Typography>
      </View>
      <View style={styles.progressColumn}>
        {millisecondsUntilAutoPublish !== null ? (
          <AgentAutoPublishCountdown
            millisecondsRemaining={millisecondsUntilAutoPublish}
          />
        ) : isTaskCompleted ? (
          <View style={styles.allDone}>
            <CheckmarkIcon
              height={16}
              stroke={theme.sk.color.icon.primary}
              width={16}
            />
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              {i18n.t('pricing.agent.tasks.all_done')}
            </Typography>
          </View>
        ) : (
          <View style={styles.progressBar}>
            <View style={styles.progressTrack}>
              {segments.map((segment, index) =>
                segment.count > 0 ? (
                  <View
                    key={index}
                    style={{
                      backgroundColor: segment.color,
                      width: widthPercent(segment.count),
                    }}
                  />
                ) : null,
              )}
            </View>
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm"
            >
              {`${Math.round(progress)}%`}
            </Typography>
          </View>
        )}
      </View>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  allDone: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  container: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceLarge,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  containerHovered: {
    borderColor: theme.sk.color.border.secondary,
  },
  descriptionColumn: {
    flex: 1,
  },
  gpImpactColumn: {
    alignItems: 'flex-end',
    minWidth: 110,
  },
  nameColumn: {
    flexShrink: 0,
    gap: theme.sk.spacing.spaceXsmall,
    width: 240,
  },
  progressBar: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    // Original short bar length — the column is wider only to fit the
    // auto-publish label, so the bar keeps its own fixed width.
    width: 128,
  },
  progressColumn: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    justifyContent: 'flex-end',
    width: 180,
  },
  progressTrack: {
    backgroundColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusRound,
    flex: 1,
    flexDirection: 'row',
    height: 6,
    overflow: 'hidden',
  },
  recCountColumn: {
    alignItems: 'flex-end',
    minWidth: 80,
  },
}));
